import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/api";
import { useLang } from "@/context/LanguageContext";
import { LogoMark, Wordmark } from "@/components/Logo";

export default function AdminLogin() {
  const { t, isAr } = useLang();
  const a = t.admin;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(() => navigate("/admin/dashboard", { replace: true })).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      navigate("/admin/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const Arrow = isAr ? ArrowRight : ArrowLeft;

  return (
    <div className="grid min-h-screen place-items-center bg-creamdeep/70 px-5 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md rounded-[2rem] border border-cardline bg-white p-8 shadow-[0_36px_72px_-36px_rgba(94,33,196,0.25)] sm:p-10">
        <Link to="/" className="mx-auto flex w-fit items-center gap-2.5" data-testid="admin-login-logo">
          <LogoMark className="h-9 w-9" />
          <Wordmark />
        </Link>
        <h1 className="mt-8 font-display text-3xl font-bold text-ink">{a.loginTitle}</h1>
        <p className="mt-1 text-sm text-inksoft">{a.loginSub}</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <label className="block">
            <span className="mb-1.5 block text-sm font-bold text-ink">{a.email}</span>
            <input
              data-testid="admin-email-input"
              type="email"
              required
              dir="ltr"
              className="field-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-bold text-ink">{a.password}</span>
            <input
              data-testid="admin-password-input"
              type="password"
              required
              dir="ltr"
              className="field-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {err && (
            <p data-testid="admin-login-error" className="rounded-xl bg-terra-light px-4 py-3 text-sm font-semibold text-terra">
              {err}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit-button"
            className="w-full rounded-xl bg-pine py-3.5 font-bold text-cream transition-colors hover:bg-pine-dark disabled:opacity-60"
          >
            {busy ? a.signingIn : a.signIn}
          </button>
        </form>

        <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm font-semibold text-inksoft transition-colors hover:text-pine">
          <Arrow className="h-4 w-4" />
          {a.back}
        </Link>
      </div>
    </div>
  );
}
